import { useEffect, useMemo, useRef, useState } from 'react';
import { HOUR_PX, layoutDay, splitAllDay } from '../../lib/calendar/timeGridLayout';
import { dayKeyParts, instantAt, minutesOf, WEEKDAY_NAMES } from '../../lib/calendar/zonedCalendar';
import { EventTagChip } from './EventTagChip';
import { AllDayBand } from './AllDayBand';

const GUTTER_PX = 62;
const SLOT_MIN = 30;
const SLOTS = (24 * 60) / SLOT_MIN;
const SLOT_PX = (HOUR_PX * SLOT_MIN) / 60;
const HOURS = Array.from({ length: 24 }, (_, h) => h);

const hourLabel = (h) => (h === 0 ? '' : `${h % 12 === 0 ? 12 : h % 12} ${h < 12 ? 'AM' : 'PM'}`);
const slotLabel = (min) => {
    const h = Math.floor(min / 60);
    const m = min % 60;
    return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};

/**
 * The Week (7 columns) and Day (1 column) time grid — one body for both, so
 * neither can drift onto its own treatment (#679).
 *
 * Replaces CalendarWeekView and CalendarDayView, which bucketed events into
 * hour ROWS by browser-local time and rendered only 7 AM–8 PM (Week) and
 * 12 AM–5 PM (Day), silently dropping everything outside those windows. Here:
 *   - all 24 hours render, and the grid scrolls to the working day on mount;
 *   - events are positioned blocks, placed in the DISPLAY zone (fold-in Q4b);
 *   - overlapping events lay out side by side; all-day events go to the band;
 *   - a drop keeps its minutes (the old Week view snapped to :00).
 *
 * Props:
 *   dayKeys: string[]            the columns, as display-zone day keys
 *   events: CalendarEvent[]
 *   displayZone: string
 *   todayKey: string
 *   onEventClick(event)
 *   onSlotClick?(startInstant: Date) — omit to disable click-to-create
 *   slotDisabledReason?: string    — shown as the slot tooltip when disabled
 *   onDayClick?(dayKey)            — the day number in a column head
 *   onRescheduleDraft({event,newStart,newEnd})
 */
export function CalendarTimeGrid({
    dayKeys, events, displayZone, todayKey, onEventClick, onSlotClick, slotDisabledReason,
    onDayClick, onRescheduleDraft,
}) {
    const scrollerRef = useRef(null);
    const [dragOver, setDragOver] = useState(null);
    const [nowMin, setNowMin] = useState(() => minutesOf(new Date(), displayZone));
    const isDay = dayKeys.length === 1;

    const { band, timed } = useMemo(() => splitAllDay(events, dayKeys, displayZone), [events, dayKeys, displayZone]);
    const layouts = useMemo(
        () => Object.fromEntries(dayKeys.map(k => [k, layoutDay(timed[k] || [])])),
        [dayKeys, timed],
    );

    // Keep the now line moving without re-rendering more than once a minute.
    useEffect(() => {
        const id = setInterval(() => setNowMin(minutesOf(new Date(), displayZone)), 60 * 1000);
        setNowMin(minutesOf(new Date(), displayZone));
        return () => clearInterval(id);
    }, [displayZone]);

    // D17: open on the working day — 7 AM, or earlier if something starts
    // earlier, or the now line when it is later in the day. Re-run when the
    // columns change (paging), not on every event refetch.
    const firstKey = dayKeys[0];
    useEffect(() => {
        const el = scrollerRef.current;
        if (!el) return;
        const starts = dayKeys.flatMap(k => (timed[k] || []).map(s => s.startMin));
        const earliest = starts.length ? Math.min(...starts) / 60 : 7;
        let hour = Math.min(earliest, 7);
        if (dayKeys.includes(todayKey) && nowMin / 60 > hour + 8) hour = nowMin / 60 - 2;
        el.scrollTop = Math.max(0, hour - 0.5) * HOUR_PX;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [firstKey, dayKeys.length]);

    const handleDrop = (e, dayKey, slotMin) => {
        e.preventDefault();
        setDragOver(null);
        const raw = e.dataTransfer.getData('application/json');
        if (!raw) return;
        try {
            const { eventId, duration } = JSON.parse(raw);
            const event = (events || []).find(ev => ev.event_id === eventId);
            if (!event) return;
            const newStart = instantAt(dayKey, slotMin, displayZone);
            const newEnd = new Date(newStart.getTime() + duration);
            onRescheduleDraft?.({ event, newStart: newStart.toISOString(), newEnd: newEnd.toISOString() });
        } catch { /* a drag from outside the calendar */ }
    };

    return (
        <div className="flex-1 min-h-0 min-w-0 flex flex-col overflow-x-auto" data-testid={isDay ? 'calendar-day-view' : 'calendar-week-view'}>
            <div className={`flex-1 min-h-0 flex flex-col ${isDay ? '' : 'min-w-[760px]'}`}>
                {/* Column heads */}
                <div className="flex flex-none border-b border-[#EDEBE6] bg-white">
                    <div className="flex-none border-r border-[#F2F0EC]" style={{ width: GUTTER_PX }} />
                    {dayKeys.map(key => {
                        const p = dayKeyParts(key);
                        const today = key === todayKey;
                        return (
                            <div
                                key={key}
                                className={`flex-1 min-w-0 box-border flex flex-col gap-0.5 px-1 pt-2 pb-[9px] border-r border-[#F2F0EC] ${isDay ? 'items-start pl-3.5' : 'items-center'} ${today ? 'bg-[#FBFAFE]' : ''}`}
                            >
                                <span className="text-[10.5px] font-semibold tracking-[0.08em] uppercase text-[#A29B95]">
                                    {WEEKDAY_NAMES[p.weekday].slice(0, 3)}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => onDayClick?.(key)}
                                    disabled={!onDayClick}
                                    aria-label={`${WEEKDAY_NAMES[p.weekday]} ${p.day}`}
                                    className={`flex items-center justify-center w-7 h-7 rounded-full text-[14.5px] ${today ? 'bg-[#4D11F6] text-white font-semibold' : 'text-[#3A3532] font-medium hover:bg-[#F4F2EE]'} disabled:hover:bg-transparent`}
                                    data-testid={`day-head-${key}`}
                                >
                                    {p.day}
                                </button>
                            </div>
                        );
                    })}
                </div>

                <AllDayBand entries={band} dayKeys={dayKeys} onEventClick={onEventClick} displayZone={displayZone} gutterPx={GUTTER_PX} />

                {/* Hours */}
                <div ref={scrollerRef} className="flex-1 min-h-0 overflow-y-auto" data-testid="calendar-time-scroller">
                    <div className="flex relative pt-2.5">
                        <div className="flex-none border-r border-[#F2F0EC]" style={{ width: GUTTER_PX }}>
                            {HOURS.map(h => (
                                <div key={h} className="relative" style={{ height: HOUR_PX }}>
                                    <span className="absolute -top-[7px] right-[9px] text-[10.5px] text-[#A29B95] whitespace-nowrap">{hourLabel(h)}</span>
                                </div>
                            ))}
                        </div>
                        {dayKeys.map(key => {
                            const today = key === todayKey;
                            return (
                                <div
                                    key={key}
                                    className={`flex-1 min-w-0 relative border-r border-[#F2F0EC] ${today ? 'bg-[#FDFCFF]' : ''}`}
                                    data-testid={`day-column-${key}`}
                                >
                                    {Array.from({ length: SLOTS }, (_, i) => {
                                        const slotMin = i * SLOT_MIN;
                                        const slotKey = `${key}-${slotMin}`;
                                        return (
                                            <div
                                                key={slotMin}
                                                onClick={onSlotClick ? () => onSlotClick(instantAt(key, slotMin, displayZone)) : undefined}
                                                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOver(slotKey); }}
                                                onDragLeave={() => setDragOver(prev => (prev === slotKey ? null : prev))}
                                                onDrop={(e) => handleDrop(e, key, slotMin)}
                                                title={onSlotClick ? `New event · ${slotLabel(slotMin)}` : slotDisabledReason}
                                                className={`box-border border-b ${slotMin % 60 ? 'border-[#F9F7F4]' : 'border-[#F4F2EE]'} ${onSlotClick ? 'cursor-pointer hover:bg-[#F6F4FF]' : ''} ${dragOver === slotKey ? 'bg-[#EFEAFE]' : ''}`}
                                                style={{ height: SLOT_PX }}
                                                data-testid={`slot-${key}-${slotMin}`}
                                            />
                                        );
                                    })}
                                    {today && (
                                        <div
                                            className="absolute left-0 right-0 h-0 border-t-2 border-[#F5354E] z-[3] pointer-events-none"
                                            style={{ top: (nowMin / 60) * HOUR_PX }}
                                            data-testid="calendar-now-line"
                                        >
                                            <span className="absolute -left-1 -top-1 w-2 h-2 rounded-full bg-[#F5354E]" />
                                        </div>
                                    )}
                                    {(layouts[key] || []).map(({ segment, top, height, lane, lanes, span }) => (
                                        <EventTagChip
                                            key={`${segment.event.event_id}-${key}`}
                                            variant="block"
                                            event={segment.event}
                                            onClick={onEventClick}
                                            displayZone={displayZone}
                                            draggable={!segment.continued}
                                            heightPx={height}
                                            style={{
                                                top,
                                                height,
                                                left: `calc(${(lane / lanes) * 100}% + 4px)`,
                                                width: `calc(${(span / lanes) * 100}% - ${lanes > 1 ? 6 : 10}px)`,
                                                zIndex: 2 + lane,
                                            }}
                                        />
                                    ))}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}

